import React, { useState, useEffect } from 'react';
import './GenericScoreSheet.css';
import useBackGuard from '../hooks/useBackGuard';

function GenericScoreSheet() {
  useBackGuard();

  const [players, setPlayers] = useState(() => {
    try {
      const saved = localStorage.getItem('generic-players');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [newPlayer, setNewPlayer] = useState('');
  const [rounds, setRounds] = useState(() => {
    try {
      const saved = localStorage.getItem('generic-rounds');
      return saved ? JSON.parse(saved) : [1];
    } catch { return [1]; }
  });
  const [scores, setScores] = useState(() => {
    try {
      const saved = localStorage.getItem('generic-scores');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [showRowTotals, setShowRowTotals] = useState(false);
  const [isLowestWins, setIsLowestWins] = useState(false);

  useEffect(() => {
    localStorage.setItem('generic-players', JSON.stringify(players));
  }, [players]);

  useEffect(() => {
    localStorage.setItem('generic-rounds', JSON.stringify(rounds));
  }, [rounds]);

  useEffect(() => {
    localStorage.setItem('generic-scores', JSON.stringify(scores));
  }, [scores]);

  // Ajouter un joueur
  const addPlayer = () => {
    if (newPlayer.trim() && !players.find(p => p.name === newPlayer.trim())) {
      const updatedPlayers = [...players, { id: Date.now().toString(), name: newPlayer.trim() }];
      setPlayers(updatedPlayers);

      // Initialiser les scores du nouveau joueur pour tous les tours existants
      const newScores = { ...scores };
      rounds.forEach(round => {
        newScores[`${updatedPlayers[updatedPlayers.length - 1].id}-${round}`] = '';
      });
      setScores(newScores);

      setNewPlayer('');
    }
  };

  // Vérifier s'il faut ajouter un nouveau tour (utilise un snapshot des scores transmis)
  const checkForNewRound = (candidateScores) => {
    if (players.length > 0) {
      const lastRound = rounds[rounds.length - 1];
      const allFilledForLastRound = players.every(player => {
        const v = candidateScores[`${player.id}-${lastRound}`];
        return v !== '' && v !== undefined && v !== '-'; // '-' considéré comme non rempli
      });
      if (allFilledForLastRound) {
        const newRound = lastRound + 1;
        setRounds(r => (r[r.length - 1] === lastRound ? [...r, newRound] : r)); // éviter doublon si appel multiple quasi simultané
      }
    }
  };

  // Mettre à jour un score
  const updateScore = (playerId, round, value) => {
    const key = `${playerId}-${round}`;
    const newScores = { ...scores, [key]: value };
    setScores(newScores);
    checkForNewRound(newScores); // Utilise l'instantané avec la valeur déjà saisie
  };

  // Obtenir le total pour un joueur
  const getPlayerTotal = (playerId) => {
    return rounds.reduce((total, round) => {
      const score = scores[`${playerId}-${round}`];
      // Autoriser '-' temporaire (non compté) et valeurs négatives
      if (score === '-' || score === '' || score === undefined) return total;
      const parsed = parseInt(score, 10);
      return total + (isNaN(parsed) ? 0 : parsed);
    }, 0);
  };

  // Calculer le total pour un tour spécifique
  const getRoundTotal = (round) => {
    return players.reduce((total, player) => {
      const score = scores[`${player.id}-${round}`];
      if (score === '-' || score === '' || score === undefined) return total;
      const parsed = parseInt(score, 10);
      return total + (isNaN(parsed) ? 0 : parsed);
    }, 0);
  };

  // Réinitialiser les scores
  const resetScores = () => {
    const newScores = {};
    players.forEach(player => {
      rounds.forEach(round => {
        newScores[`${player.id}-${round}`] = '';
      });
    });
    setScores(newScores);
    setRounds([1]); // Revenir au premier tour
  };

  // Réinitialiser les joueurs et les scores
  const resetPlayers = () => {
    setPlayers([]);
    setScores({});
    setRounds([1]);
    setNewPlayer('');
    localStorage.removeItem('generic-players');
    localStorage.removeItem('generic-rounds');
    localStorage.removeItem('generic-scores');
  };

  // Obtenir le classement des joueurs avec logique inversée
  const getPlayerRanking = () => {
    return [...players]
      .map(player => ({
        id: player.id,
        name: player.name,
        total: getPlayerTotal(player.id)
      }))
      .sort((a, b) => isLowestWins ? a.total - b.total : b.total - a.total);
  };

  // Basculer l'affichage des totaux par ligne
  const toggleRowTotals = () => {
    setShowRowTotals(!showRowTotals);
  };

  // Basculer la logique de victoire
  const toggleWinLogic = () => {
    setIsLowestWins(!isLowestWins);
  };

  return (
    <div className="generic-score-sheet">
      <h1>Feuille de Score générique</h1>

      <div className="player-control-buttons">
        <input
          type="text"
          placeholder="Nom du joueur"
          value={newPlayer}
          onChange={e => setNewPlayer(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addPlayer()}
        />
        <button onClick={addPlayer}>Ajouter</button>
        <button onClick={resetScores} style={{ background: '#f59e42', color: 'white' }}>
          Effacer scores
        </button>
        <button onClick={resetPlayers} style={{ background: '#ef4444', color: 'white' }}>
          Réinitialiser joueurs
        </button>
      </div>

      {players.length > 0 && (
        <div className="score-options">
          <button
            onClick={toggleRowTotals}
            className={`toggle-button ${showRowTotals ? 'active' : ''}`}
          >
            {showRowTotals ? 'Masquer totaux par tour' : 'Afficher totaux par tour'}
          </button>
          <button
            onClick={toggleWinLogic}
            className={`toggle-button win-logic ${isLowestWins ? 'active' : ''}`}
          >
            {isLowestWins ? 'Plus petit score gagne' : 'Plus grand score gagne'}
          </button>
        </div>
      )}

      {players.length > 0 && (
        <div className="score-table-container">
          <table className="score-table">
            <thead>
              <tr>
                <th>Tour</th>
                {players.map(player => (
                  <th key={player.id}>{player.name}</th>
                ))}
                {showRowTotals && <th className="round-total">Total du tour</th>}
              </tr>
            </thead>
            <tbody>
              {rounds.map(round => (
                <tr key={round}>
                  <td>{round}</td>
                  {players.map(player => (
                    <td key={`${player.id}-${round}`}>
                      <div className="score-input-container">
                        <input
                          type="text"
                          value={scores[`${player.id}-${round}`] || ''}
                          onChange={e => {
                            const val = e.target.value;
                            if (/^-?$/.test(val) || /^-?\d+$/.test(val)) {
                              updateScore(player.id, round, val);
                            }
                          }}
                          inputMode="numeric"
                          placeholder="0"
                        />
                        <button
                          type="button"
                          className="sign-toggle"
                          onClick={() => {
                            const key = `${player.id}-${round}`;
                            const current = scores[key] || '';
                            if (current === '' || current === '-') {
                              updateScore(player.id, round, '-');
                            } else if (/^\-\d+$/.test(current)) {
                              updateScore(player.id, round, current.slice(1));
                            } else if (/^\d+$/.test(current)) {
                              updateScore(player.id, round, '-' + current);
                            } else {
                              // Valeur inattendue -> ne rien faire
                            }
                          }}
                          title="Basculer signe"
                        >±</button>
                      </div>
                    </td>
                  ))}
                  {showRowTotals && (
                    <td className="round-total">{getRoundTotal(round)}</td>
                  )}
                </tr>
              ))}
              <tr className="total-row">
                <td>Total</td>
                {players.map(player => (
                  <td key={`${player.id}-total`}>{getPlayerTotal(player.id)}</td>
                ))}
                {showRowTotals && <td className="round-total">-</td>}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {players.length > 0 && (
        <div className="ranking-section">
          <h2>Classement {isLowestWins && '(Plus petit score gagne)'}</h2>
          <table className="ranking-table">
            <thead>
              <tr>
                <th>Position</th>
                <th>Joueur</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {getPlayerRanking().map((player, index) => (
                <tr key={player.id}>
                  <td>{index + 1}</td>
                  <td>{player.name}</td>
                  <td>{player.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default GenericScoreSheet;
